import {
  collection,
  doc,
  getDoc,
  getDocs,
  addDoc,
  updateDoc,
  deleteDoc,
  query,
  orderBy,
  limit,
  where,
  Timestamp,
  writeBatch,
} from 'firebase/firestore';
import { getFirebaseDb } from './firebase';
import type {
  Session,
  SessionMessage,
  SessionContextLoaded,
  SessionOutputs,
  SessionStateSnapshot,
  SessionType,
  SessionModality,
  SessionExtraction,
} from '../types';

// ─── Path Helpers ────────────────────────────────────────────────────────────

const paths = {
  sessions: (userId: string) => collection(getFirebaseDb(), 'users', userId, 'sessions'),
  session: (userId: string, sessionId: string) =>
    doc(getFirebaseDb(), 'users', userId, 'sessions', sessionId),
  messages: (userId: string, sessionId: string) =>
    collection(getFirebaseDb(), 'users', userId, 'sessions', sessionId, 'messages'),
  message: (userId: string, sessionId: string, messageId: string) =>
    doc(getFirebaseDb(), 'users', userId, 'sessions', sessionId, 'messages', messageId),
};

// ─── Sessions ────────────────────────────────────────────────────────────────

export async function createSession(
  userId: string,
  type: SessionType,
  modality: SessionModality,
  contextLoaded: SessionContextLoaded
): Promise<string> {
  const emptyExtraction: SessionExtraction = {
    nodesCreated: [],
    nodesReinforced: [],
    edgesCreated: [],
    edgesRevised: [],
    motifsIdentified: [],
    contradictionsFound: [],
  };

  const sessionData: Omit<Session, 'id'> = {
    userId,
    type,
    modality,
    status: 'active',
    startedAt: Timestamp.now(),
    contextLoaded,
    extraction: emptyExtraction,
    outputs: {},
    stateSnapshot: { mood: 0, energy: 0, physicalState: '' },
    messageCount: 0,
  };

  const ref = await addDoc(paths.sessions(userId), sessionData);
  return ref.id;
}

export async function getSession(userId: string, sessionId: string): Promise<Session | null> {
  const snap = await getDoc(paths.session(userId, sessionId));
  if (!snap.exists()) return null;
  return { id: snap.id, ...snap.data() } as Session;
}

export async function getRecentSessions(userId: string, count = 10): Promise<Session[]> {
  const q = query(paths.sessions(userId), orderBy('startedAt', 'desc'), limit(count));
  const snap = await getDocs(q);
  return snap.docs.map(d => ({ id: d.id, ...d.data() } as Session));
}

export async function getSessionsForDateRange(
  userId: string,
  startDate: Date,
  endDate: Date
): Promise<Session[]> {
  const start = Timestamp.fromDate(startDate);
  const end = Timestamp.fromDate(endDate);
  const q = query(
    paths.sessions(userId),
    where('startedAt', '>=', start),
    where('startedAt', '<', end),
    orderBy('startedAt', 'asc')
  );
  const snap = await getDocs(q);
  return snap.docs.map(d => ({ id: d.id, ...d.data() } as Session));
}

export async function updateSession(
  userId: string,
  sessionId: string,
  updates: Partial<Omit<Session, 'id' | 'userId' | 'startedAt'>>
): Promise<void> {
  await updateDoc(paths.session(userId, sessionId), updates);
}

export async function updateSessionStateSnapshot(
  userId: string,
  sessionId: string,
  updates: Partial<SessionStateSnapshot>
): Promise<void> {
  const dotUpdates: Record<string, unknown> = {};
  for (const [key, value] of Object.entries(updates)) {
    if (value === undefined) continue;
    dotUpdates[`stateSnapshot.${key}`] = value;
  }
  if (Object.keys(dotUpdates).length === 0) return;
  await updateDoc(paths.session(userId, sessionId), dotUpdates);
}

export async function updateSessionJournalNarrative(
  userId: string,
  session: Session,
  journalNarrative: string
): Promise<void> {
  await updateDoc(paths.session(userId, session.id), {
    outputs: {
      ...(session.outputs ?? {}),
      journalNarrative,
    },
  });
}

export async function completeSession(
  userId: string,
  sessionId: string,
  outputs: SessionOutputs,
  stateSnapshot: SessionStateSnapshot,
  extraction: SessionExtraction
): Promise<void> {
  const startSnap = await getDoc(paths.session(userId, sessionId));
  const startedAt = startSnap.data()?.startedAt as Timestamp | undefined;
  const endedAt = Timestamp.now();
  const durationSeconds = startedAt
    ? Math.round(endedAt.toMillis() / 1000 - startedAt.toMillis() / 1000)
    : undefined;

  await updateDoc(paths.session(userId, sessionId), {
    status: 'completed',
    endedAt,
    durationSeconds,
    outputs,
    stateSnapshot,
    extraction,
  });
}

export async function abandonSession(userId: string, sessionId: string): Promise<void> {
  await updateDoc(paths.session(userId, sessionId), {
    status: 'abandoned',
    endedAt: Timestamp.now(),
  });
}

// ─── Messages ────────────────────────────────────────────────────────────────

export async function addMessage(
  userId: string,
  sessionId: string,
  role: 'user' | 'assistant',
  content: string,
  index: number
): Promise<string> {
  const ref = await addDoc(paths.messages(userId, sessionId), {
    index,
    role,
    content,
    timestamp: Timestamp.now(),
  } satisfies Omit<SessionMessage, 'id'>);

  // Increment messageCount on session
  await updateDoc(paths.session(userId, sessionId), {
    messageCount: index + 1,
  });

  return ref.id;
}

export async function getMessages(userId: string, sessionId: string): Promise<SessionMessage[]> {
  const q = query(paths.messages(userId, sessionId), orderBy('index', 'asc'));
  const snap = await getDocs(q);
  return snap.docs.map(d => ({ id: d.id, ...d.data() } as SessionMessage));
}

/** Returns messages formatted as { role, content } pairs for Claude API. */
export async function getMessagesForLLM(
  userId: string,
  sessionId: string
): Promise<Array<{ role: 'user' | 'assistant'; content: string }>> {
  const messages = await getMessages(userId, sessionId);
  return messages.map(m => ({ role: m.role, content: m.content }));
}

// ─── Session Deletion ─────────────────────────────────────────────────────────

export interface DeleteSessionResult {
  messagesDeleted: number;
  nodesDeleted: number;
  nodesUpdated: number;
  edgesDeleted: number;
  edgesUpdated: number;
  motifsDeleted: number;
  motifsUpdated: number;
  tasksDeleted: number;
  dayFocusDeleted: number;
}

/**
 * Fully deletes a session and cascading cleans all data it produced:
 * belief nodes/edges/motifs, task plans, and messages.
 *
 * For nodes created by this session: deleted if no other sessions reinforce them,
 * otherwise just the reinforcement from this session is removed.
 * For edges: sessionId removed from evidence; deleted if no evidence remains.
 * For motifs: observations from this session removed; deleted if none remain.
 */
export async function deleteSession(
  userId: string,
  sessionId: string
): Promise<DeleteSessionResult> {
  const db = getFirebaseDb();
  const session = await getSession(userId, sessionId);
  if (!session) throw new Error('Session not found');

  const result: DeleteSessionResult = {
    messagesDeleted: 0,
    nodesDeleted: 0,
    nodesUpdated: 0,
    edgesDeleted: 0,
    edgesUpdated: 0,
    motifsDeleted: 0,
    motifsUpdated: 0,
    tasksDeleted: 0,
    dayFocusDeleted: 0,
  };

  const extraction = session.extraction;

  // 1. Clean up belief nodes created by this session
  const allCreatedNodeIds = extraction.nodesCreated ?? [];
  const nodesToPrune: string[] = [];
  for (const nodeId of allCreatedNodeIds) {
    const nodeSnap = await getDoc(doc(db, 'users', userId, 'nodes', nodeId));
    if (!nodeSnap.exists()) continue;
    const node = nodeSnap.data();
    const otherReinforcements = (node.reinforcements ?? []).filter(
      (r: { sessionId: string }) => r.sessionId !== sessionId
    );
    if (otherReinforcements.length === 0) {
      nodesToPrune.push(nodeId);
    } else {
      await updateDoc(doc(db, 'users', userId, 'nodes', nodeId), {
        reinforcements: otherReinforcements,
        updatedAt: Timestamp.now(),
      });
      result.nodesUpdated++;
    }
  }

  // 2. Clean up reinforced nodes (remove reinforcement entries from this session)
  const reinforcedNodeIds = extraction.nodesReinforced ?? [];
  for (const nodeId of reinforcedNodeIds) {
    if (nodesToPrune.includes(nodeId)) continue;
    const nodeSnap = await getDoc(doc(db, 'users', userId, 'nodes', nodeId));
    if (!nodeSnap.exists()) continue;
    const node = nodeSnap.data();
    const filtered = (node.reinforcements ?? []).filter(
      (r: { sessionId: string }) => r.sessionId !== sessionId
    );
    const tierHistory = (node.tierHistory ?? []).filter(
      (t: { sessionId: string }) => t.sessionId !== sessionId
    );
    await updateDoc(doc(db, 'users', userId, 'nodes', nodeId), {
      reinforcements: filtered,
      tierHistory,
      updatedAt: Timestamp.now(),
    });
    result.nodesUpdated++;
  }

  // 3. Prune nodes that should be fully deleted (+ connected edges/motifs)
  // Collect all edges and motifs to decide what to batch-delete
  for (const nodeId of nodesToPrune) {
    const [sourceEdges, targetEdges] = await Promise.all([
      getDocs(query(collection(db, 'users', userId, 'edges'), where('sourceNodeId', '==', nodeId))),
      getDocs(query(collection(db, 'users', userId, 'edges'), where('targetNodeId', '==', nodeId))),
    ]);
    const batch = writeBatch(db);
    batch.delete(doc(db, 'users', userId, 'nodes', nodeId));
    result.nodesDeleted++;

    for (const edgeDoc of [...sourceEdges.docs, ...targetEdges.docs]) {
      batch.delete(edgeDoc.ref);
      result.edgesDeleted++;
    }
    await batch.commit();
  }

  // 4. Clean up edges created by this session (that weren't already deleted above)
  const createdEdgeIds = extraction.edgesCreated ?? [];
  for (const edgeId of createdEdgeIds) {
    const edgeSnap = await getDoc(doc(db, 'users', userId, 'edges', edgeId));
    if (!edgeSnap.exists()) continue;
    const edge = edgeSnap.data();
    const filtered = (edge.evidence ?? []).filter((s: string) => s !== sessionId);
    if (filtered.length === 0) {
      await deleteDoc(doc(db, 'users', userId, 'edges', edgeId));
      result.edgesDeleted++;
    } else {
      await updateDoc(doc(db, 'users', userId, 'edges', edgeId), {
        evidence: filtered,
        updatedAt: Timestamp.now(),
      });
      result.edgesUpdated++;
    }
  }

  // 5. Clean up motifs identified in this session
  const motifIds = extraction.motifsIdentified ?? [];
  for (const motifId of motifIds) {
    const motifSnap = await getDoc(doc(db, 'users', userId, 'motifs', motifId));
    if (!motifSnap.exists()) continue;
    const motif = motifSnap.data();
    const filtered = (motif.observations ?? []).filter(
      (o: { sessionId: string }) => o.sessionId !== sessionId
    );
    if (filtered.length === 0) {
      await deleteDoc(doc(db, 'users', userId, 'motifs', motifId));
      result.motifsDeleted++;
    } else {
      await updateDoc(doc(db, 'users', userId, 'motifs', motifId), {
        observations: filtered,
        frequency: filtered.length,
        lastSeen: Timestamp.now(),
      });
      result.motifsUpdated++;
    }
  }

  // 6. Delete associated task plans
  if (session.outputs?.taskPlanId) {
    const taskRef = doc(db, 'users', userId, 'tasks', session.outputs.taskPlanId);
    const taskSnap = await getDoc(taskRef);
    if (taskSnap.exists()) {
      await deleteDoc(taskRef);
      result.tasksDeleted++;
    }
  }

  // 6b. Delete associated day focus docs created by this session
  if (Array.isArray(session.outputs?.dayFocusIds)) {
    for (const dayFocusId of session.outputs.dayFocusIds) {
      const focusRef = doc(db, 'users', userId, 'dayFocus', dayFocusId);
      const focusSnap = await getDoc(focusRef);
      if (focusSnap.exists()) {
        await deleteDoc(focusRef);
        result.dayFocusDeleted++;
      }
    }
  }

  // 7. Delete all messages in the subcollection
  const messagesSnap = await getDocs(paths.messages(userId, sessionId));
  const messageBatchSize = 450;
  for (let i = 0; i < messagesSnap.docs.length; i += messageBatchSize) {
    const batch = writeBatch(db);
    const chunk = messagesSnap.docs.slice(i, i + messageBatchSize);
    for (const msgDoc of chunk) {
      batch.delete(msgDoc.ref);
    }
    await batch.commit();
    result.messagesDeleted += chunk.length;
  }

  // 8. Delete the session document itself
  await deleteDoc(paths.session(userId, sessionId));

  return result;
}
